import { Link } from "react-router-dom";
import { Icon } from "../icons/Icon";
import { Aviso, Brasao } from "../components/Layout";
import { fmt, type Edital } from "../lib/api";

/**
 * Página inicial do processo.
 *
 * A abertura é tratada como a identificação de um edital, não como hero de
 * produto: a ficha ao lado do título traz os dados que um aviso oficial
 * sempre declara primeiro (órgão, município, ano letivo, faixa salarial).
 * Cronograma, cargos e critério de pontuação seguem o mesmo princípio:
 * tabela e lista de dados, não cartão decorativo.
 */
export function Landing({ edital }: { edital: Edital }) {
  const inscricao = edital.cronograma.find((f) => f.codigo === "INSCRICAO");
  const aberta = inscricao?.aberta ?? false;
  const futura = inscricao ? new Date(inscricao.inicio) > new Date(edital.agora) : false;

  const diasRestantes = inscricao?.fim
    ? Math.max(0, Math.ceil((new Date(inscricao.fim).getTime() - new Date(edital.agora).getTime()) / 86_400_000))
    : null;
  const dataFim = inscricao?.fim ? new Date(inscricao.fim).toLocaleDateString("pt-BR") : null;

  const remuneracoes = edital.cargos.map((c) => c.remuneracao);
  const remMin = Math.min(...remuneracoes);
  const remMax = Math.max(...remuneracoes);
  const faixaSalarial = remMin === remMax
    ? fmt.moeda(remMin)
    : `${fmt.moeda(remMin)} a ${fmt.moeda(remMax)}`;

  type Cargo = (typeof edital.cargos)[number];
  type Componente = Cargo["componentes"][number];
  const linhasCargos: Array<{ cargo: Cargo; componente: Componente | null }> = [];
  for (const cargo of edital.cargos) {
    if (cargo.componentes.length > 0) {
      for (const componente of cargo.componentes) linhasCargos.push({ cargo, componente });
    } else {
      linhasCargos.push({ cargo, componente: null });
    }
  }

  return (
    <>
      {/* ---------------------------------------------------------- masthead */}
      <section className="masthead">
        <div className="container masthead-grade">
          <div className="masthead-texto">
            <p className="masthead-selo">
              <Brasao tamanho={28} className="masthead-brasao" />
              <span>Edital SEMED nº 005/{edital.anoLetivo}</span>
            </p>

            <h1 className="display">
              Educadores Habilitados e Monitores para a rede municipal
            </h1>

            <p className="lead medida">
              Contratação em designação temporária para a educação básica de{" "}
              {edital.municipio}/{edital.uf}, ano letivo de {edital.anoLetivo}. A seleção é
              por avaliação de títulos, experiência e qualificação profissional, de caráter
              classificatório.
            </p>

            <div className="masthead-cta">
              {aberta ? (
                <Link to="/inscricao" className="cta-principal">
                  <span>Fazer inscrição</span>
                  <span className="cta-seta" aria-hidden="true"><Icon nome="direita" tamanho={20} /></span>
                </Link>
              ) : (
                <span className="cta-principal cta-inativo" aria-disabled="true">
                  <span>{futura ? "Inscrições ainda não abriram" : "Inscrições encerradas"}</span>
                </span>
              )}

              {aberta && diasRestantes !== null && (
                <p className="cta-prazo">
                  <span className="cta-prazo-numero num">{diasRestantes}</span>
                  <span>{diasRestantes === 1 ? "dia" : "dias"} para o encerramento,<br />em {dataFim}</span>
                </p>
              )}
            </div>

            <a className="masthead-doc" href={encodeURI(edital.arquivoPdfPath)} target="_blank" rel="noreferrer">
              <Icon nome="arquivo" tamanho={18} />
              <span>Ler o edital completo</span>
              <span className="masthead-doc-tipo">PDF</span>
            </a>
          </div>

          <aside className="ficha-edital" aria-label="Dados do edital">
            {inscricao && (
              <div className="ficha-prazo">
                <span className={`ficha-estado${aberta ? " ficha-estado-aberto" : ""}`}>
                  <span className="ficha-ponto" aria-hidden="true" />
                  {aberta ? "Inscrições abertas" : futura ? "Inscrições em breve" : "Inscrições encerradas"}
                </span>
                <p className="ficha-prazo-valor num">{inscricao.periodoFormatado}</p>
              </div>
            )}
            <dl>
              <div><dt>Órgão</dt><dd>{edital.orgao}</dd></div>
              <div><dt>Município</dt><dd>{edital.municipio}/{edital.uf}</dd></div>
              <div><dt>Ano letivo</dt><dd>{edital.anoLetivo}</dd></div>
              <div><dt>Cargos</dt><dd>{edital.cargos.length}</dd></div>
              <div><dt>Remuneração</dt><dd>{faixaSalarial}</dd></div>
            </dl>
          </aside>
        </div>
      </section>

      {inscricao && !aberta && (
        <section style={{ background: "var(--color-surface)", paddingBlock: "var(--space-6)" }}>
          <div className="container">
            <Aviso tipo="atencao">
              {futura ? (
                <>
                  O período de inscrição vai de <strong>{inscricao.periodoFormatado}</strong>.
                  Fora dessa janela o sistema não aceita inscrição, nem por e-mail,
                  correspondência ou qualquer outra via.
                </>
              ) : (
                <>
                  O período de inscrição ({inscricao.periodoFormatado}) está encerrado.
                  Acompanhe as próximas etapas no cronograma abaixo.
                </>
              )}
            </Aviso>
          </div>
        </section>
      )}

      {/* -------------------------------------------------------- cronograma */}
      <section className="secao">
        <div className="container grade grade-dividida">
          <div className="pilha pilha-6">
            <div>
              <h2>Cronograma</h2>
              <p className="texto-secundario">
                É responsabilidade do candidato acompanhar as publicações oficiais.
              </p>
            </div>

            <ol className="cronograma">
              {edital.cronograma.map((fase, i) => {
                const estado = fase.aberta ? "atual" : fase.encerrada ? "concluida" : "futura";
                const limpa = (t: string) => t.replace(/\.$/, "").trim();
                const obs = fmt.semRefs(fase.observacao);
                const repete = obs !== "" && limpa(obs) === limpa(fase.periodoFormatado);
                return (
                  <li key={fase.codigo} className={`marco marco-${estado}`}
                      aria-current={fase.aberta ? "step" : undefined}>
                    <span className="marco-ponto" aria-hidden="true">
                      {fase.encerrada ? <Icon nome="checado" tamanho={14} /> : String(i + 1)}
                    </span>
                    <div className="marco-corpo">
                      <p className="marco-rotulo">
                        {fase.rotulo}
                        {fase.aberta && <span className="situacao situacao-DEFERIDA">em andamento</span>}
                      </p>
                      <p className="marco-data num">{fase.periodoFormatado}</p>
                      {obs && !repete && (
                        <p className="texto-sutil marco-obs">{obs}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="pilha pilha-6">
            <div className="cartao pilha pilha-4">
              <h3>Reserva de vagas</h3>
              <div className="cotas">
                {edital.cotas.map((cota) => (
                  <div key={cota.tipo} className="cota">
                    <p className="cota-valor num">{fmt.pontos(cota.percentual).replace(",00", "")}<span>%</span></p>
                    <p className="cota-rotulo">{cota.rotulo}</p>
                    <p className="texto-sutil">
                      Posições {cota.posicoes.join(", ")} e seguintes, de{" "}
                      {cota.passoContinuacao} em {cota.passoContinuacao}, na Classificação Geral.
                    </p>
                  </div>
                ))}
              </div>
              <p className="texto-sutil">
                Vagas reservadas não preenchidas revertem para a ampla concorrência.
              </p>
            </div>

            <div className="cartao pilha pilha-2">
              <h3>Limite de inscrições</h3>
              <p className="texto-sm">
                Você pode fazer até <strong>{edital.maxInscricoesTotal} inscrições</strong>:
                3 para Educador de Educação Básica, 1 para Educador Especialista Pedagógico
                e 1 para Monitor.
              </p>
              <p className="texto-sutil">
                No máximo 2 contratos podem ser celebrados por candidato.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ cargos */}
      <section className="secao" style={{ background: "var(--color-surface)" }}>
        <div className="container pilha pilha-8">
          <div>
            <h2>Cargos e pré-requisitos</h2>
            <p className="texto-secundario medida">
              Confira o pré-requisito do componente antes de se inscrever: é o que mais causa
              indeferimento. Para efeito de classificação, a formação exigida como
              pré-requisito não entra na contagem de pontos.
            </p>
          </div>

          <div className="painel">
            <div className="tabela-rolagem">
              <table className="tabela tabela-cartoes tabela-cargos">
                <caption className="so-leitor">Cargos, carga horária, remuneração e pré-requisito</caption>
                <thead>
                  <tr>
                    <th scope="col">Código</th>
                    <th scope="col">Cargo</th>
                    <th scope="col">Carga horária</th>
                    <th scope="col" className="col-num">Remuneração</th>
                    <th scope="col">Pré-requisito</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasCargos.map(({ cargo, componente }) => (
                    <tr key={componente ? componente.id : cargo.id}>
                      <td data-rotulo="Código"><span className="etiqueta">{cargo.codigo}</span></td>
                      <td data-rotulo="Cargo">
                        <strong>{cargo.nome}</strong>
                        {cargo.componentes.length > 1 && componente && (
                          <span className="texto-sutil" style={{ display: "block" }}>
                            {componente.nome}
                          </span>
                        )}
                        <span className="texto-sutil" style={{ display: "block" }}>
                          {cargo.campoAtuacao}
                        </span>
                      </td>
                      <td className="texto-sutil" data-rotulo="Carga horária">{cargo.cargaHoraria}</td>
                      <td className="col-num" data-rotulo="Remuneração">{fmt.moeda(cargo.remuneracao)}</td>
                      <td data-rotulo="Pré-requisito">
                        {componente?.preRequisito}
                        {fmt.semRefs(componente?.observacao) && (
                          <span className="texto-sutil" style={{ display: "block" }}>
                            {fmt.semRefs(componente?.observacao)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- pontuação */}
      <section className="secao">
        <div className="container grade grade-2">
          <div className="pilha pilha-4">
            <div>
              <h2>Como a pontuação é calculada</h2>
              <p className="texto-secundario medida">
                O sistema calcula e mostra a conta enquanto você preenche, para que não haja
                surpresa na classificação.
              </p>
            </div>

            <table className="quadro-pontuacao">
              <caption className="so-leitor">Critérios de pontuação</caption>
              <thead>
                <tr>
                  <th scope="col">Critério</th>
                  <th scope="col" className="col-num">Pontuação</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Experiência na rede municipal de Baixo Guandu</td>
                  <td className="col-num">0,2 pt/mês</td>
                </tr>
                <tr>
                  <td>Experiência em outras redes</td>
                  <td className="col-num">0,1 pt/mês</td>
                </tr>
                <tr>
                  <td>Teto da soma de experiência</td>
                  <td className="col-num">30 meses</td>
                </tr>
                <tr>
                  <td>
                    Qualificação profissional
                    <span className="texto-sutil" style={{ display: "block" }}>
                      Um título por categoria, sem cumulatividade
                    </span>
                  </td>
                  <td className="col-num">Por título</td>
                </tr>
              </tbody>
            </table>

            <p className="texto-sm texto-secundario">
              Critério de desempate: maior pontuação de qualificação; depois maior pontuação de
              experiência; depois maior idade.
            </p>
          </div>

          <div className="cartao pilha pilha-2">
            <h3>As quatro listas</h3>
            <dl className="pilha pilha-2" style={{ margin: 0 }}>
              <div>
                <dt><strong>Ampla concorrência</strong></dt>
                <dd className="texto-sm texto-secundario" style={{ margin: 0 }}>
                  Todos os candidatos, inclusive PcD e negros, em suas posições de mérito.
                </dd>
              </div>
              <div>
                <dt><strong>Pessoas com Deficiência</strong></dt>
                <dd className="texto-sm texto-secundario" style={{ margin: 0 }}>Só os inscritos nessa modalidade.</dd>
              </div>
              <div>
                <dt><strong>Candidatos negros</strong></dt>
                <dd className="texto-sm texto-secundario" style={{ margin: 0 }}>Só os inscritos nessa modalidade.</dd>
              </div>
              <div>
                <dt><strong>Classificação Geral</strong></dt>
                <dd className="texto-sm texto-secundario" style={{ margin: 0 }}>
                  Ordem usada para convocação, já com a alternância das reservas aplicada.
                </dd>
              </div>
            </dl>
            <Link to="/listas" className="btn btn-secundario btn-pequeno" style={{ marginTop: "var(--space-3)" }}>
              <Icon nome="lista" tamanho={16} />
              Ver listas publicadas
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
